import React from "react";
import { Card, CardBody } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { Button } from "@heroui/react";
import homeAboutPhoto from "../assets/images/home-about.jpg"

interface AboutSectionProps {
  onViewMoreClick: () => void;
}

export const AboutSection: React.FC<AboutSectionProps> = ({ onViewMoreClick }) => {
  const advantages = [
    {
      icon: "lucide:heart",
      title: "Мы ценим каждого клиента",
      description: "Индивидуальный подход к каждому покупателю, внимание к вашим потребностям и пожеланиям"
    },
    {
      icon: "lucide:leaf",
      title: "Натуральные материалы",
      description: "Используем только натуральные высококачественные материалы для создания наших продуктов"
    },
    {
      icon: "lucide:fingerprint",
      title: "Каждый продукт индивидуален",
      description: "Все наши изделия создаются вручную, делая каждый продукт уникальным"
    },
    {
      icon: "lucide:recycle",
      title: "Мы за эко",
      description: "Экологичная упаковка и безопасные для окружающей среды компоненты"
    },
    {
      icon: "lucide:message-square",
      title: "Положительные отзывы",
      description: "Множество довольных клиентов подтверждают высокое качество нашей продукции"
    },
    {
      icon: "lucide:helping-hand",
      title: "Помощь с выбором",
      description: "Мы всегда готовы помочь вам подобрать идеальный аромат"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <section className="py-24 bg-content2" id="about">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-6">О нашем <span className="font-medium">бренде</span></h2>
            <p className="text-foreground/80 mb-6">
              Мы создаем премиальные ароматические продукты ручной работы, которые наполняют пространство уютом и гармонией. Наша миссия — сделать каждый день особенным через ароматы, которые пробуждают эмоции и создают атмосферу.
            </p>
            <p className="text-foreground/80 mb-6">
              Мы верим, что аромат — это больше, чем просто запах. Это история, настроение и воспоминания, которые остаются с вами надолго.
            </p>
            
            <Button 
              color="primary" 
              variant="flat" 
              endContent={<Icon icon="lucide:arrow-right" width={16} />}
              onPress={onViewMoreClick}
            >
              Узнать больше
            </Button>
          </motion.div>
          
          <motion.div 
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="relative h-[500px] overflow-hidden">
              <img 
                src={homeAboutPhoto}
                alt="О нашем бренде" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-primary p-6 max-w-xs">
              <p className="text-sm italic text-primary-foreground">
                "Аромат – это первое, что мы чувствуем, и последнее, что мы запоминаем"
                – Сафонова Анна основатель бренда ароматической продукции SAFONOVA
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-24"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {advantages.map((advantage, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Card className="border border-divider bg-content1 h-full" disableRipple>
                <CardBody className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/5 p-3 rounded-full shrink-0">
                      <Icon icon={advantage.icon} className="text-primary" width={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">{advantage.title}</h3>
                      <p className="text-foreground/70 text-sm">{advantage.description}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};